import { useRef, useState } from "react";
import { problems } from "@/mocks/problems";

export default function Hero() {
  const [q, setQ] = useState("");
  const [focused, setFocused] = useState(false);
  const [noResult, setNoResult] = useState(false);
  const timer = useRef<number | null>(null);

  const results = q.trim()
    ? problems.filter((p) => p.title.includes(q.trim()) || p.keyword.includes(q.trim()) || p.brief.includes(q.trim()))
    : [];

  const jump = (id: string) => {
    setNoResult(false);
    setQ("");
    setFocused(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const submit = () => {
    if (!q.trim()) return;
    if (results.length > 0) {
      jump(results[0].id);
    } else {
      setNoResult(true);
      setFocused(false);
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setNoResult(false), 2600);
    }
  };

  const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section className="relative flex min-h-[560px] w-full items-center overflow-hidden md:min-h-[640px]">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage:
            "url(https://readdy.ai/api/search-image?query=Stylized%20comic%20book%20inspired%20abstract%20illustration%2C%20playful%20hand%20drawn%20comic%20panels%20with%20halftone%20dots%20and%20speed%20lines%2C%20warm%20coral%20orange%20teal%20green%20and%20amber%20palette%2C%20creative%20vector%20art%2C%20clean%20minimal%20composition%2C%20soft%20balanced%20lighting%2C%20editorial%20quality&width=1920&height=1080&seq=bibi-hero-01&orientation=landscape)",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/45 to-black/70" />

      <div className="relative z-10 mx-auto w-full max-w-5xl px-4 pb-16 pt-28 text-center md:px-6 md:pb-20 md:pt-32">
        <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-1.5 text-sm text-white backdrop-blur-sm">
          <i className="ri-hashtag text-secondary-300" />
          常见问题一站式解决
        </span>
        <h1 className="mt-6 text-3xl font-black leading-tight text-white md:text-5xl md:leading-tight">
          笔笔漫画网打不开、加载慢？
          <br className="hidden md:block" />
          <span className="font-display text-secondary-300">来这里，几步就解决</span>
        </h1>
        <p className="mx-auto mt-5 max-w-2xl text-sm leading-7 text-white/85 md:text-base">
          换源失败、收藏丢失、搜索无结果、登录异常……我们把最常见的阅读问题整理成了清晰可操作的排查步骤，
          按提示操作即可恢复顺畅阅读，收藏、搜索、登录一步到位。
        </p>

        <div className="relative mx-auto mt-8 max-w-xl">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:rounded-full sm:bg-white/95 sm:p-1.5">
            <div className="relative flex-1">
              <i className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-base text-foreground-600" />
              <input
                type="text"
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setNoResult(false);
                }}
                onFocus={() => setFocused(true)}
                onBlur={() => setTimeout(() => setFocused(false), 180)}
                onKeyDown={(e) => e.key === "Enter" && submit()}
                placeholder="输入问题，如「换源失败」「收藏丢失」"
                className="w-full rounded-full bg-white/95 py-3 pl-11 pr-4 text-sm text-foreground-900 outline-none ring-1 ring-transparent transition placeholder:text-foreground-600/70 focus:ring-2 focus:ring-secondary-400 sm:bg-transparent sm:ring-0"
              />
              {focused && q.trim() && results.length > 0 && (
                <div className="absolute left-0 right-0 top-full z-30 mt-2 overflow-hidden rounded-2xl border border-background-200 bg-background-50 shadow-soft">
                  {results.map((r) => (
                    <button
                      key={r.id}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        jump(r.id);
                      }}
                      className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm text-foreground-900 hover:bg-background-100"
                    >
                      <i className={`${r.icon} text-lg text-primary-500`} />
                      {r.title}
                      <span className="ml-auto rounded-full bg-background-100 px-2 py-0.5 text-xs text-foreground-600">
                        {r.keyword}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={submit}
              className="whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
            >
              立即排查
            </button>
          </div>
          {noResult && (
            <p className="mt-3 rounded-xl bg-white/15 px-4 py-2 text-sm text-white">
              没有直接匹配的问题，你可以试试更简短的关键词，或浏览下方六大常见问题。
            </p>
          )}
        </div>

        <div className="mx-auto mt-7 flex max-w-2xl flex-wrap items-center justify-center gap-2">
          <span className="text-sm text-white/70">热门：</span>
          {["打不开", "加载慢", "换源", "收藏", "搜索", "登录"].map((k) => {
            const p = problems.find((x) => x.keyword === k);
            return (
              <button
                key={k}
                onClick={() => p && jump(p.id)}
                className="whitespace-nowrap rounded-full border border-white/25 bg-white/10 px-3 py-1.5 text-sm text-white backdrop-blur-sm transition-colors hover:bg-white/25"
              >
                #{k}
              </button>
            );
          })}
        </div>

        <div className="mx-auto mt-10 flex max-w-xl flex-col items-center gap-3 sm:flex-row">
          <button
            onClick={() => scrollTo("problems")}
            className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-white px-5 py-3 text-sm font-bold text-foreground-900 transition-colors hover:bg-secondary-100 sm:w-auto"
          >
            <i className="ri-list-check-2" />
            查看六大问题
          </button>
          <button
            onClick={() => scrollTo("solutions")}
            className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full border border-white/30 px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-white/10 sm:w-auto"
          >
            <i className="ri-tools-line" />
            直达解决方案
          </button>
        </div>
      </div>
    </section>
  );
}